import { Badge } from "../ui/badge";
import { useState } from "react";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  rescueEvidenceAnalysisRequestSchema,
  type RescueEvidenceAnalysisRequest,
  type RescueEvidenceAnalysisResponse,
} from "@io-spoke/contracts/rescue";
import { Check, CircleSlash2, History, LoaderCircle, Quote, ScanText, ShieldCheck } from "lucide-react";
import { AiFeedbackControl } from "@components/ai/AiFeedbackControl";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@components/ui/sheet";
import { Textarea } from "@components/ui/textarea";

type Props = {
  open: boolean;
  customerName: string;
  pending: boolean;
  approving: boolean;
  error: string | null;
  result: RescueEvidenceAnalysisResponse | null;
  onOpenChange: (open: boolean) => void;
  onAnalyze: (input: RescueEvidenceAnalysisRequest) => void | Promise<void>;
  onApprove: () => void | Promise<void>;
  onReset: () => void;
};

type ProposalResult = Extract<RescueEvidenceAnalysisResponse, { outcome: "proposal" }>;
type AbstainedResult = Extract<RescueEvidenceAnalysisResponse, { outcome: "abstained" }>;
const evidenceFormSchema = rescueEvidenceAnalysisRequestSchema.omit({ occurredAt: true });
type EvidenceFormInput = z.input<typeof evidenceFormSchema>;
type EvidenceFormValues = z.output<typeof evidenceFormSchema>;

function ProposalReview({ result }: { result: ProposalResult }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Review proposal</span>
          <Badge variant="outline">Estimated confidence {Math.round(result.inference.confidence * 100)}%</Badge>
        </div>
        <h3 className="mt-3 text-lg font-semibold">{result.proposal.risk.title}</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{result.proposal.risk.promise}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground">Suggested owner</div>
          <div className="mt-1 text-sm font-medium">{result.ownerRecommendation.owner}</div>
          <div className="mt-1 text-xs text-muted-foreground">Estimated owner confidence {Math.round(result.ownerRecommendation.confidence * 100)}%</div>
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground">Risk classification</div>
          <div className="mt-1 text-sm font-medium">{result.proposal.risk.kind.replace(/_/g, " ")}</div>
        </div>
      </div>
      <div className="rounded-lg border bg-muted/25 p-4" aria-label="Evidence supporting this proposal">
        <div className="flex items-center gap-2 text-sm font-medium"><Quote className="h-4 w-4 text-violet-500" aria-hidden="true" />Evidence used</div>
        <ul className="mt-3 space-y-2">
          {result.citations.map((citation) => (
            <li key={`${citation.evidenceId}-${citation.quote}`} className="rounded-md border bg-background p-3">
              <blockquote className="text-sm leading-6">“{citation.quote}”</blockquote>
              <p className="mt-2 text-xs text-muted-foreground">Supports title, priority, and customer impact</p>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border bg-muted/25 p-4" aria-label="Owner recommendation evidence">
        <div className="flex items-center gap-2 text-sm font-medium"><History className="h-4 w-4 text-violet-500" aria-hidden="true" />Why this owner</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{result.ownerRecommendation.rationale}</p>
        {result.ownerRecommendation.evidence.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {result.ownerRecommendation.evidence.map((item) => (
              <li key={item.entityId} className="rounded-md border bg-background p-3">
                <div className="text-sm font-medium">{item.title}</div>
                {item.systems.length > 0 ? <div className="mt-1 text-xs text-muted-foreground">Systems: {item.systems.join(", ")}</div> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <AnalysisStatus result={result} />
      <AiFeedbackControl responseId={result.inference.responseId} />
    </div>
  );
}

function AbstentionReview({ result }: { result: AbstainedResult }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-5">
        <CircleSlash2 className="h-6 w-6 text-violet-500" aria-hidden="true" />
        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Analysis complete</p>
        <h3 className="mt-2 text-lg font-semibold">No supportable obligation found</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{result.abstention.reason}</p>
      </div>
      <div className="rounded-lg border bg-muted/25 p-4">
        <div className="text-sm font-medium">Evidence considered</div>
        {result.abstention.evidenceConsidered.map((evidence) => (
          <div key={evidence.evidenceId} className="mt-3 rounded-md border bg-background p-3">
            <p className="text-xs font-medium text-muted-foreground">{evidence.sourceLabel}</p>
            <p className="mt-1 text-sm leading-6">{evidence.excerpt}</p>
          </div>
        ))}
      </div>
      <AnalysisStatus result={result} />
      <AiFeedbackControl responseId={result.inference.responseId} />
    </div>
  );
}

function AnalysisStatus({ result }: { result: RescueEvidenceAnalysisResponse }) {
  const reduced = result.inference.fallbackUsed;
  let message = "Analysis completed and is ready for your review.";
  if (result.outcome === "abstained") message = "IO Spoke stopped without creating a proposal because the evidence did not support a reliable conclusion.";
  else if (reduced) message = "IO Spoke prepared a conservative proposal because full analysis was unavailable. Review it carefully.";
  const color = reduced
    ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
    : "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200";
  return <div className={`rounded-md border p-3 text-sm ${color}`} role="status">{message}{result.inference.redactionApplied ? " Sensitive values were redacted." : ""}</div>;
}

export function EvidenceLabSheet({ open, customerName, pending, approving, error, result, onOpenChange, onAnalyze, onApprove, onReset }: Props) {
  const {
    register, control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EvidenceFormInput, unknown, EvidenceFormValues>({
    resolver: zodResolver(evidenceFormSchema),
    mode: "onSubmit",
    defaultValues: {
      evidenceText: "",
      sourceLabel: "Customer conversation",
      channel: "call",
    },
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const analysisAction = useAsyncAction();
  const approvalAction = useAsyncAction();
  const evidenceLength = String(watch("evidenceText") ?? "").length;
  const submitEvidence = (values: EvidenceFormValues) => analysisAction.run(async () => {
    setActionError(null);
    try { await onAnalyze({ ...values, occurredAt: new Date().toISOString() }); }
    catch { setActionError("The evidence could not be analyzed. Try again."); }
  });

  const approveProposal = () => approvalAction.run(async () => {
    setActionError(null);
    try { await onApprove(); }
    catch { setActionError("The rescue could not be created. Try again."); }
  });
  const busy = pending || approving || analysisAction.pending || approvalAction.pending;

  return (
    <Sheet open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <SheetContent side="right" className="flex h-full w-full flex-col overflow-y-auto [&>button]:h-11 [&>button]:w-11 sm:max-w-xl">
        <SheetHeader className="pr-8">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-violet-600 dark:text-violet-300"><ScanText className="h-3.5 w-3.5" /> Evidence Lab</div>
          <SheetTitle className="text-xl">Analyze evidence for {customerName}</SheetTitle>
          <SheetDescription className="leading-6">Paste sanitized customer evidence. IO Spoke will prepare a proposal only when the evidence supports one. Nothing is saved until you approve it.</SheetDescription>
        </SheetHeader>
        <form className="flex flex-1 flex-col" onSubmit={handleSubmit(submitEvidence)} noValidate>
        <div className="my-6 flex-1 space-y-5">
          {!result ? <>
            <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
              <div className="space-y-2"><Label htmlFor="evidence-source">Source label</Label><Input id="evidence-source" {...register("sourceLabel")} maxLength={200} disabled={pending} aria-invalid={Boolean(errors.sourceLabel)} aria-describedby={errors.sourceLabel ? "evidence-source-error" : undefined} />{errors.sourceLabel ? <p id="evidence-source-error" role="alert" className="text-xs text-destructive">{errors.sourceLabel.message}</p> : null}</div>
              <div className="space-y-2"><Label htmlFor="evidence-channel">Channel</Label><FormChoiceSelect id="evidence-channel" control={control} name="channel" disabled={pending} options={[{ value: "call", label: "Call" }, { value: "email", label: "Email" }, { value: "support", label: "Support" }, { value: "slack", label: "Slack" }]} /></div>
            </div>
            <div className="space-y-2"><Label htmlFor="evidence-text">Customer evidence</Label><Textarea id="evidence-text" {...register("evidenceText")} maxLength={8000} disabled={pending} className="min-h-[240px] resize-y" placeholder="Paste a sanitized call note, email, support exchange, or Slack conversation…" aria-invalid={Boolean(errors.evidenceText)} aria-describedby={errors.evidenceText ? "evidence-help evidence-text-error" : "evidence-help"} /><div id="evidence-help" className="flex justify-between gap-4 text-xs text-muted-foreground"><span>Minimum 50 characters. Remove anything you do not want analyzed.</span><span className="tabular-nums">{evidenceLength}/8000</span></div>{errors.evidenceText ? <p id="evidence-text-error" role="alert" className="text-xs text-destructive">{errors.evidenceText.message}</p> : null}</div>
            <div className="flex gap-3 rounded-md border bg-muted/35 p-3 text-xs leading-5 text-muted-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" /><p>Common emails, phone numbers, access tokens, and secrets are redacted on the server before inference. Always review a proposal before approval.</p></div>
          </> : null}
          {result?.outcome === "proposal" ? <ProposalReview result={result} /> : null}
          {result?.outcome === "abstained" ? <AbstentionReview result={result} /> : null}
          {pending ? <div className="flex items-center gap-3 rounded-md border border-violet-300 bg-violet-50 p-3 text-sm text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-200" role="status" aria-live="polite"><LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />Interpreting evidence and checking whether it supports a proposal…</div> : null}
          {error || actionError ? <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" role="alert">{error ?? actionError}</div> : null}
        </div>
        <SheetFooter className="gap-2 border-t border-border/60 pt-4 sm:space-x-0">
          <Button type="button" variant="outline" onClick={() => result ? onReset() : onOpenChange(false)} disabled={busy}>{result ? "Edit evidence" : "Cancel"}</Button>
          {result?.outcome === "proposal" ? <Button type="button" onClick={() => void approveProposal()} loading={approving || approvalAction.pending} loadingLabel="Creating rescue…" className="bg-orange-600 text-white hover:bg-orange-700"><Check className="h-4 w-4" />Approve and create rescue</Button> : null}
          {!result ? <Button type="submit" loading={pending || analysisAction.pending} loadingLabel="Analyzing…" className="bg-violet-600 text-white hover:bg-violet-700"><ScanText className="h-4 w-4" />Analyze evidence</Button> : null}
        </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
